/**
 * DeliveryZonesContent - Gestão de bairros/zonas de entrega, taxas e mapa
 * Extraído VERBATIM do App.js (Fase 2 da refatoração — zero mudança de comportamento).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Ban, CheckCircle, ChevronDown, Download, Edit, FileText, MapPin, Navigation, Package, PlusCircle, RefreshCw, Settings, Store, Trash2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import DeliveryZonesMap from '../DeliveryZonesMap';
import ShippingPromoConfig from '../ShippingPromoConfig';
import { getBackendUrl } from '../../utils/api';

const API = `${getBackendUrl()}/api`;

const DeliveryZonesContent = () => {
  const restaurant = JSON.parse(localStorage.getItem('restaurant') || '{}');
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    fee: '',
    active: true
  });

  // ============ ESTADOS PARA MODO RAIO KM ============
  const [deliveryConfig, setDeliveryConfig] = useState({
    mode: 'neighborhood', // 'neighborhood' ou 'radius'
    store_address: {
      cep: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      latitude: null,
      longitude: null
    },
    radius_zones: [],
    max_delivery_radius: null,
    exclusion_zones: [],  // Zonas de exclusão (polígonos)
    delivery_enabled: true,  // Modalidade: Delivery habilitado
    pickup_enabled: true     // Modalidade: Retirada habilitada
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeSuccess, setGeocodeSuccess] = useState(false);
  const [isRadiusZoneModalOpen, setIsRadiusZoneModalOpen] = useState(false);
  const [editingRadiusZone, setEditingRadiusZone] = useState(null);
  const [radiusZoneForm, setRadiusZoneForm] = useState({
    min_distance: '',
    max_distance: '',
    fee: ''
  });
  const [showExclusionMapModal, setShowExclusionMapModal] = useState(false);  // Modal do mapa de exclusões

  useEffect(() => {
    fetchZones();
    fetchDeliveryConfig();
  }, []);

  // Carregar configuração de entrega
  const fetchDeliveryConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/delivery-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setDeliveryConfig({
          mode: response.data.mode || 'neighborhood',
          store_address: response.data.store_address || {
            cep: '', street: '', number: '', complement: '',
            neighborhood: '', city: '', state: '', latitude: null, longitude: null
          },
          radius_zones: response.data.radius_zones || [],
          max_delivery_radius: response.data.max_delivery_radius,
          exclusion_zones: response.data.exclusion_zones || [],
          delivery_enabled: response.data.delivery_enabled !== false,
          pickup_enabled: response.data.pickup_enabled !== false
        });
        if (response.data.store_address?.latitude) {
          setGeocodeSuccess(true);
        }
      }
    } catch (error) {
      console.error('Error fetching delivery config:', error);
    }
  };

  // Salvar configuração de entrega
  const saveDeliveryConfig = async () => {
    try {
      setSavingConfig(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API}/delivery-config`, deliveryConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Configuração de entrega salva com sucesso!');
      fetchDeliveryConfig(); // Recarregar para pegar coordenadas atualizadas
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setSavingConfig(false);
    }
  };

  // Geocodificar endereço da loja
  const geocodeStoreAddress = async () => {
    try {
      setGeocoding(true);
      setGeocodeSuccess(false);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/delivery-config/geocode`, deliveryConfig.store_address, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDeliveryConfig(prev => ({
        ...prev,
        store_address: {
          ...prev.store_address,
          latitude: response.data.latitude,
          longitude: response.data.longitude
        }
      }));
      setGeocodeSuccess(true);
      alert(`Endereço localizado! Coordenadas: ${response.data.latitude.toFixed(6)}, ${response.data.longitude.toFixed(6)}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao localizar endereço. Verifique os dados informados.');
    } finally {
      setGeocoding(false);
    }
  };

  // Adicionar/Editar faixa de raio
  const openRadiusZoneModal = (zone = null) => {
    if (zone) {
      setEditingRadiusZone(zone);
      setRadiusZoneForm({
        min_distance: zone.min_distance,
        max_distance: zone.max_distance,
        fee: zone.fee
      });
    } else {
      setEditingRadiusZone(null);
      // Sugerir próxima faixa baseada nas existentes
      const existingZones = deliveryConfig.radius_zones;
      const lastMax = existingZones.length > 0 
        ? Math.max(...existingZones.map(z => z.max_distance)) 
        : 0;
      setRadiusZoneForm({
        min_distance: lastMax,
        max_distance: lastMax + 3,
        fee: ''
      });
    }
    setIsRadiusZoneModalOpen(true);
  };

  const handleRadiusZoneSubmit = (e) => {
    e.preventDefault();
    const newZone = {
      id: editingRadiusZone?.id || `rz-${Date.now()}`,
      min_distance: parseFloat(radiusZoneForm.min_distance),
      max_distance: parseFloat(radiusZoneForm.max_distance),
      fee: parseFloat(radiusZoneForm.fee)
    };

    // Validações
    if (newZone.min_distance >= newZone.max_distance) {
      alert('A distância mínima deve ser menor que a máxima');
      return;
    }
    if (newZone.fee < 0) {
      alert('A taxa não pode ser negativa');
      return;
    }

    setDeliveryConfig(prev => {
      const updatedZones = editingRadiusZone
        ? prev.radius_zones.map(z => z.id === editingRadiusZone.id ? newZone : z)
        : [...prev.radius_zones, newZone];
      
      // Ordenar por distância mínima
      updatedZones.sort((a, b) => a.min_distance - b.min_distance);
      
      return { ...prev, radius_zones: updatedZones };
    });

    setIsRadiusZoneModalOpen(false);
  };

  const deleteRadiusZone = (zoneId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta faixa de distância?')) return;
    setDeliveryConfig(prev => ({
      ...prev,
      radius_zones: prev.radius_zones.filter(z => z.id !== zoneId)
    }));
  };

  const fetchZones = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/delivery-zones`);
      setZones(response.data);
    } catch (error) {
      console.error('Error fetching zones:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (zone = null) => {
    if (zone) {
      setEditingZone(zone);
      setFormData({
        name: zone.name,
        fee: zone.fee,
        active: zone.available !== undefined ? zone.available : zone.active
      });
    } else {
      setEditingZone(null);
      setFormData({ name: '', fee: '', active: true });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingZone) {
        await axios.put(`${API}/delivery-zones/${editingZone.id}`, formData);
      } else {
        await axios.post(`${API}/delivery-zones`, formData);
      }
      setIsModalOpen(false);
      fetchZones();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao salvar bairro');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este bairro?')) return;
    
    try {
      await axios.delete(`${API}/delivery-zones/${id}`);
      fetchZones();
    } catch (error) {
      alert('Erro ao excluir bairro');
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/delivery-zones/export?format=${format}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = format.toLowerCase();
      link.setAttribute('download', `bairros_taxas_${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Erro ao exportar bairros. Verifique se você está logado.');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Selecione um arquivo CSV');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/delivery-zones/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      setImportFile(null);
      fetchZones();
      
      setTimeout(() => {
        setIsImportModalOpen(false);
        setImportResult(null);
      }, 3000);
    } catch (error) {
      alert('Erro ao importar bairros: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = (format = 'csv') => {
    if (format === 'csv') {
      const template = `bairro,taxa
Centro,5.00
Jardim América,7.50
Vila Nova,10.00
São João,8.00
Santa Cruz,12.00
Bela Vista,6.50`;

      const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_bairros.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else {
      // For XLSX, we'll trigger download from backend
      const template = `bairro,taxa
Centro,5.00
Jardim América,7.50
Vila Nova,10.00
São João,8.00
Santa Cruz,12.00
Bela Vista,6.50`;
      
      // Create a simple XLSX using client-side generation
      // We'll use the same approach as export but with template data
      alert('Para template XLSX, baixe um export existente ou use o template CSV');
      downloadTemplate('csv');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Taxas de Entrega</h1>
            <p className="text-sm text-gray-400 mt-1">
              Configure como calcular a taxa de entrega
            </p>
          </div>
        </div>

        {/* Toggle: Modo de Entrega */}
        <div className="glass-card p-4">
          {/* SEÇÃO: Modalidades de Pedido */}
          <div className="mb-6 pb-4 border-b border-gray-700/50">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
              <Package size={16} />
              Modalidades de Pedido
            </h3>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deliveryConfig.delivery_enabled !== false}
                  onChange={(e) => {
                    if (!e.target.checked && !deliveryConfig.pickup_enabled) {
                      toast.error('Pelo menos uma modalidade deve estar ativa');
                      return;
                    }
                    setDeliveryConfig(prev => ({ ...prev, delivery_enabled: e.target.checked }));
                  }}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500"
                />
                <span className="text-sm text-gray-300">🛵 Delivery</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deliveryConfig.pickup_enabled !== false}
                  onChange={(e) => {
                    if (!e.target.checked && !deliveryConfig.delivery_enabled) {
                      toast.error('Pelo menos uma modalidade deve estar ativa');
                      return;
                    }
                    setDeliveryConfig(prev => ({ ...prev, pickup_enabled: e.target.checked }));
                  }}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500"
                />
                <span className="text-sm text-gray-300">🏪 Retirada no local</span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2">Desmarque para ocultar a opção no checkout do cliente</p>
          </div>

          {/* SEÇÃO: Modo de Cálculo */}
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Settings size={18} />
            Modo de Cálculo
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => setDeliveryConfig(prev => ({ ...prev, mode: 'neighborhood' }))}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                deliveryConfig.mode === 'neighborhood' 
                  ? 'border-green-500 bg-green-500/10' 
                  : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  deliveryConfig.mode === 'neighborhood' ? 'bg-green-500' : 'bg-gray-700'
                }`}>
                  <MapPin size={20} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">Por Bairro</p>
                  <p className="text-xs text-gray-400">Taxa fixa por bairro cadastrado</p>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => setDeliveryConfig(prev => ({ ...prev, mode: 'radius' }))}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                deliveryConfig.mode === 'radius' 
                  ? 'border-blue-500 bg-blue-500/10' 
                  : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  deliveryConfig.mode === 'radius' ? 'bg-blue-500' : 'bg-gray-700'
                }`}>
                  <Navigation size={20} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">Por Raio (KM)</p>
                  <p className="text-xs text-gray-400">Taxa calculada pela distância</p>
                </div>
              </div>
            </button>
          </div>
          
          {/* Botão Salvar Modo */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={saveDeliveryConfig}
              disabled={savingConfig}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all disabled:opacity-50"
            >
              {savingConfig ? 'Salvando...' : 'Salvar Configuração'}
            </button>
          </div>
        </div>
      </div>

      {/* ============ MODO POR BAIRRO ============ */}
      {deliveryConfig.mode === 'neighborhood' && (
        <>
          {/* Header Bairros */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-white">Bairros Cadastrados</h2>
              <p className="text-sm text-gray-400">
                {zones.length} {zones.length === 1 ? 'bairro' : 'bairros'}
              </p>
            </div>
            <button 
              onClick={() => openModal()} 
              className="glass-button-primary w-full sm:w-auto py-3 px-6 text-base font-medium"
              data-testid="new-zone-button"
            >
              <PlusCircle size={20} className="mr-2" />
              Novo Bairro
            </button>
          </div>
        
          {/* Import/Export Buttons */}
          <div className="flex flex-wrap gap-3">
            {/* Export Dropdown */}
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-colors"
                data-testid="export-zones-button"
              >
                <Download size={18} />
                <span className="text-sm font-medium">Exportar</span>
                <ChevronDown size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-gray-900 border-gray-700">
              <DropdownMenuItem 
                onClick={() => handleExport('csv')}
                className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              >
                <FileText size={16} className="mr-2" />
                Exportar como CSV
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleExport('xlsx')}
                className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              >
                <FileText size={16} className="mr-2" />
                Exportar como XLSX
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Import Button */}
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-colors"
            data-testid="import-zones-button"
          >
            <Upload size={18} />
            <span className="text-sm font-medium">Importar CSV/XLSX</span>
          </button>
          
          {/* Template Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-colors"
                data-testid="template-zones-button"
              >
                <FileText size={18} />
                <span className="text-sm font-medium">Baixar Template</span>
                <ChevronDown size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-gray-900 border-gray-700">
              <DropdownMenuItem 
                onClick={() => downloadTemplate('csv')}
                className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              >
                <FileText size={16} className="mr-2" />
                Template CSV
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => downloadTemplate('xlsx')}
                className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              >
                <FileText size={16} className="mr-2" />
                Template XLSX
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>

          {/* Zones List */}
          <div className="glass-card rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Bairro</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Taxa de Entrega</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Status</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/50">
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-gray-400">
                        Carregando bairros...
                      </td>
                    </tr>
                  ) : zones.length === 0 ? (
                    <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-400">
                    Nenhum bairro cadastrado
                  </td>
                </tr>
              ) : (
                zones.map((zone) => (
                  <tr 
                    key={zone.id} 
                    className="hover:bg-gray-800/30 transition-colors"
                    data-testid={`zone-row-${zone.id}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                          <MapPin className="text-white" size={20} />
                        </div>
                        <span className="text-white font-medium">{zone.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-green-400 font-semibold text-lg">
                        R$ {zone.fee.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                        zone.active ? 'bg-green-400/20 text-green-400' : 'bg-red-400/20 text-red-400'
                      }`}>
                        {zone.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openModal(zone)}
                          className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-400/20 rounded-lg transition-colors"
                          title="Editar"
                          data-testid={`edit-zone-${zone.id}`}
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(zone.id)}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/20 rounded-lg transition-colors"
                          title="Excluir"
                          data-testid={`delete-zone-${zone.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="glass-modal max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {editingZone ? 'Editar Bairro' : 'Novo Bairro'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Nome do Bairro *
              </label>
              <input
                type="text"
                required
                className="glass-input w-full text-base py-3 px-4"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ex: Centro"
                data-testid="zone-name-input"
              />
            </div>

            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Taxa de Entrega (R$) *
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-base">
                  R$
                </span>
                <input
                  type="number"
                  step="0.01"
                  required
                  className="glass-input w-full text-base py-3 pl-12 pr-4"
                  value={formData.fee}
                  onChange={(e) => setFormData({...formData, fee: e.target.value})}
                  placeholder="0.00"
                  data-testid="zone-fee-input"
                />
              </div>
            </div>

            <div className="bg-gray-800/30 rounded-xl p-4 flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="zone-active" className="text-base font-medium text-gray-300 block cursor-pointer">
                  Bairro Ativo
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Disponível para entregas
                </p>
              </div>
              <Switch
                id="zone-active"
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({...formData, active: checked})}
                data-testid="zone-active-switch"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-gray-800">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="w-full sm:w-auto px-6 py-3 text-base text-gray-400 hover:text-white transition-colors rounded-xl border border-gray-700 hover:border-gray-600"
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                className="w-full sm:w-auto glass-button-primary py-3 px-8 text-base font-medium"
                data-testid="zone-submit-button"
              >
                {editingZone ? '✓ Salvar' : '+ Criar Bairro'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
        <DialogContent className="glass-modal max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Importar Bairros</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-blue-400 mb-2">📋 Formato Aceito (CSV ou XLSX):</h3>
              <ul className="text-xs text-gray-300 space-y-1">
                <li>• <strong>Colunas obrigatórias:</strong> bairro, taxa</li>
                <li>• <strong>Exemplo:</strong></li>
                <li className="ml-4">bairro,taxa</li>
                <li className="ml-4">Centro,5.00</li>
                <li className="ml-4">Vila Nova,7.50</li>
                <li className="mt-2">• <strong>Comportamento:</strong> Se o bairro já existe, apenas o preço será atualizado</li>
              </ul>
            </div>

            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Selecionar Arquivo (CSV ou XLSX)
              </label>
              <input
                type="file"
                accept=".csv,.xlsx"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-teal-500 file:text-white hover:file:bg-teal-600 cursor-pointer"
                data-testid="import-zone-file-input"
              />
              {importFile && (
                <p className="text-sm text-green-400 mt-2">
                  ✓ Arquivo: {importFile.name}
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800 transition-colors"
                  >
                    <FileText size={18} />
                    <span>Baixar Template</span>
                    <ChevronDown size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-gray-900 border-gray-700">
                  <DropdownMenuItem 
                    onClick={() => downloadTemplate('csv')}
                    className="text-gray-300 hover:bg-gray-800 cursor-pointer"
                  >
                    <FileText size={16} className="mr-2" />
                    Template CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => downloadTemplate('xlsx')}
                    className="text-gray-300 hover:bg-gray-800 cursor-pointer"
                  >
                    <FileText size={16} className="mr-2" />
                    Template XLSX
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {importResult && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-green-400 mb-2">✓ Importação Concluída!</h3>
                <ul className="text-xs text-gray-300 space-y-1">
                  <li>• {importResult.created} bairros criados</li>
                  <li>• {importResult.updated} bairros atualizados</li>
                </ul>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-red-400">Erros:</p>
                    {importResult.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-300">- {err}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsImportModalOpen(false);
                  setImportFile(null);
                  setImportResult(null);
                }}
                className="flex-1 px-4 py-3 text-base text-gray-400 hover:text-white transition-colors rounded-xl border border-gray-700"
                disabled={importing}
              >
                Cancelar
              </button>
              <button
                onClick={handleImport}
                disabled={!importFile || importing}
                className="flex-1 glass-button-primary py-3 px-6 text-base font-medium disabled:opacity-50"
                data-testid="import-zone-upload-button"
              >
                {importing ? 'Importando...' : 'Importar'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
        </>
      )}

      {/* ============ MODO POR RAIO KM ============ */}
      {deliveryConfig.mode === 'radius' && (
        <div className="space-y-6">
          {/* Endereço da Loja */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Store size={20} />
              Endereço da Loja (Ponto de Referência)
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Este endereço será usado como ponto central para calcular a distância até o cliente.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">CEP</label>
                <input
                  type="text"
                  value={deliveryConfig.store_address?.cep || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    store_address: { ...prev.store_address, cep: e.target.value }
                  }))}
                  placeholder="00000-000"
                  className="glass-input w-full"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-1">Rua/Avenida *</label>
                <input
                  type="text"
                  value={deliveryConfig.store_address?.street || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    store_address: { ...prev.store_address, street: e.target.value }
                  }))}
                  placeholder="Nome da rua"
                  className="glass-input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Número *</label>
                <input
                  type="text"
                  value={deliveryConfig.store_address?.number || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    store_address: { ...prev.store_address, number: e.target.value }
                  }))}
                  placeholder="123"
                  className="glass-input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Complemento</label>
                <input
                  type="text"
                  value={deliveryConfig.store_address?.complement || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    store_address: { ...prev.store_address, complement: e.target.value }
                  }))}
                  placeholder="Sala, Loja, etc"
                  className="glass-input w-full"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Bairro *</label>
                <input
                  type="text"
                  value={deliveryConfig.store_address?.neighborhood || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    store_address: { ...prev.store_address, neighborhood: e.target.value }
                  }))}
                  placeholder="Nome do bairro"
                  className="glass-input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Cidade *</label>
                <input
                  type="text"
                  value={deliveryConfig.store_address?.city || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    store_address: { ...prev.store_address, city: e.target.value }
                  }))}
                  placeholder="Nome da cidade"
                  className="glass-input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Estado *</label>
                <select
                  value={deliveryConfig.store_address?.state || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    store_address: { ...prev.store_address, state: e.target.value }
                  }))}
                  className="glass-input w-full"
                  required
                >
                  <option value="">Selecione</option>
                  <option value="AC">AC</option>
                  <option value="AL">AL</option>
                  <option value="AP">AP</option>
                  <option value="AM">AM</option>
                  <option value="BA">BA</option>
                  <option value="CE">CE</option>
                  <option value="DF">DF</option>
                  <option value="ES">ES</option>
                  <option value="GO">GO</option>
                  <option value="MA">MA</option>
                  <option value="MT">MT</option>
                  <option value="MS">MS</option>
                  <option value="MG">MG</option>
                  <option value="PA">PA</option>
                  <option value="PB">PB</option>
                  <option value="PR">PR</option>
                  <option value="PE">PE</option>
                  <option value="PI">PI</option>
                  <option value="RJ">RJ</option>
                  <option value="RN">RN</option>
                  <option value="RS">RS</option>
                  <option value="RO">RO</option>
                  <option value="RR">RR</option>
                  <option value="SC">SC</option>
                  <option value="SP">SP</option>
                  <option value="SE">SE</option>
                  <option value="TO">TO</option>
                </select>
              </div>
            </div>
            
            {/* Botão Localizar + Status */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                onClick={geocodeStoreAddress}
                disabled={geocoding || !deliveryConfig.store_address?.street || !deliveryConfig.store_address?.city}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {geocoding ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Localizando...
                  </>
                ) : (
                  <>
                    <MapPin size={16} />
                    Localizar no Mapa
                  </>
                )}
              </button>
              
              {geocodeSuccess && deliveryConfig.store_address?.latitude && (
                <div className="flex items-center gap-2 text-green-400 text-sm">
                  <CheckCircle size={16} />
                  <span>
                    Localizado: {deliveryConfig.store_address.latitude.toFixed(4)}, {deliveryConfig.store_address.longitude.toFixed(4)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Faixas de Distância */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Navigation size={20} />
                Faixas de Distância e Taxas
              </h3>
              <button
                onClick={() => openRadiusZoneModal()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all flex items-center gap-2"
              >
                <PlusCircle size={16} />
                Nova Faixa
              </button>
            </div>
            
            {deliveryConfig.radius_zones.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Navigation size={40} className="mx-auto mb-3 opacity-50" />
                <p>Nenhuma faixa de distância configurada</p>
                <p className="text-sm">Clique em &quot;Nova Faixa&quot; para adicionar</p>
              </div>
            ) : (
              <div className="space-y-3">
                {deliveryConfig.radius_zones.map((zone, index) => (
                  <div
                    key={zone.id}
                    className="flex items-center justify-between p-4 bg-gray-800/50 rounded-xl border border-gray-700"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center text-blue-400 font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {zone.min_distance} km até {zone.max_distance} km
                        </p>
                        <p className="text-sm text-gray-400">
                          Taxa: R$ {zone.fee.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openRadiusZoneModal(zone)}
                        className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-all"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => deleteRadiusZone(zone.id)}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Raio Máximo */}
            <div className="mt-6 pt-6 border-t border-gray-700">
              <label className="block text-sm text-gray-400 mb-2">
                Raio Máximo de Entrega (km) - Opcional
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  value={deliveryConfig.max_delivery_radius || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    max_delivery_radius: e.target.value ? parseFloat(e.target.value) : null
                  }))}
                  placeholder="Ex: 15"
                  className="glass-input w-32"
                  min="0"
                  step="0.5"
                />
                <span className="text-sm text-gray-400">
                  Endereços além deste raio serão recusados automaticamente
                </span>
              </div>
            </div>
          </div>
          
          {/* Seção de Zonas de Exclusão */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Ban size={20} className="text-red-400" />
                  Zonas de Exclusão
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  Áreas dentro do raio de entrega onde você NÃO entrega
                </p>
              </div>
              <button
                onClick={() => setShowExclusionMapModal(true)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-all flex items-center gap-2"
              >
                <Edit size={16} />
                Gerenciar no Mapa
              </button>
            </div>
            
            {/* Lista de zonas de exclusão existentes */}
            {deliveryConfig.exclusion_zones && deliveryConfig.exclusion_zones.length > 0 ? (
              <div className="space-y-2">
                {deliveryConfig.exclusion_zones.map((ez) => (
                  <div
                    key={ez.id}
                    className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg border border-red-500/30"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center">
                        <Ban size={16} className="text-red-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{ez.name}</p>
                        {ez.reason && (
                          <p className="text-sm text-gray-400">{ez.reason}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const updated = deliveryConfig.exclusion_zones.filter(z => z.id !== ez.id);
                        setDeliveryConfig(prev => ({ ...prev, exclusion_zones: updated }));
                      }}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-400">
                <MapPin size={32} className="mx-auto mb-2 opacity-50" />
                <p>Nenhuma zona de exclusão definida</p>
                <p className="text-sm">Todas as áreas dentro do raio de entrega estão liberadas</p>
              </div>
            )}
          </div>
          
          {/* Botão Salvar */}
          <div className="flex justify-end">
            <button
              onClick={saveDeliveryConfig}
              disabled={savingConfig}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {savingConfig ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle size={18} />
                  Salvar Configuração
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <ShippingPromoConfig />

      {/* Modal: Nova/Editar Faixa de Raio */}
      <Dialog open={isRadiusZoneModalOpen} onOpenChange={setIsRadiusZoneModalOpen}>
        <DialogContent className="glass-modal max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {editingRadiusZone ? 'Editar Faixa de Distância' : 'Nova Faixa de Distância'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRadiusZoneSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">De (km) *</label>
                <input
                  type="number"
                  value={radiusZoneForm.min_distance}
                  onChange={(e) => setRadiusZoneForm(prev => ({
                    ...prev,
                    min_distance: e.target.value
                  }))}
                  placeholder="0"
                  className="glass-input w-full"
                  min="0"
                  step="0.5"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Até (km) *</label>
                <input
                  type="number"
                  value={radiusZoneForm.max_distance}
                  onChange={(e) => setRadiusZoneForm(prev => ({
                    ...prev,
                    max_distance: e.target.value
                  }))}
                  placeholder="5"
                  className="glass-input w-full"
                  min="0"
                  step="0.5"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Taxa de Entrega (R$) *</label>
              <input
                type="number"
                value={radiusZoneForm.fee}
                onChange={(e) => setRadiusZoneForm(prev => ({
                  ...prev,
                  fee: e.target.value
                }))}
                placeholder="5.00"
                className="glass-input w-full"
                min="0"
                step="0.50"
                required
              />
            </div>
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setIsRadiusZoneModalOpen(false)}
                className="flex-1 px-4 py-3 text-gray-400 hover:text-white transition-colors rounded-xl border border-gray-700"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 glass-button-primary py-3 px-6 font-medium"
              >
                {editingRadiusZone ? 'Salvar' : 'Adicionar'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal: Mapa de Zonas de Exclusão */}
      <Dialog open={showExclusionMapModal} onOpenChange={setShowExclusionMapModal}>
        <DialogContent className="glass-modal max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <Ban size={24} className="text-red-400" />
              Gerenciar Zonas de Exclusão
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <DeliveryZonesMap
              restaurantId={restaurant?.id}
              initialLocation={deliveryConfig.store_address?.latitude && deliveryConfig.store_address?.longitude ? {
                lat: deliveryConfig.store_address.latitude,
                lng: deliveryConfig.store_address.longitude
              } : null}
              initialZones={deliveryConfig.radius_zones?.map((z, idx) => ({
                id: idx + 1,
                name: `Zona ${idx + 1}`,
                radius: z.max_distance || z.radius || 5,
                fee: z.fee || 0,
                time: z.time_minutes || 30,
                active: true
              })) || []}
              initialExclusionZones={deliveryConfig.exclusion_zones || []}
              onSave={(config) => {
                // Atualizar estado local com as exclusões salvas
                setDeliveryConfig(prev => ({
                  ...prev,
                  exclusion_zones: config.exclusion_zones || []
                }));
                setShowExclusionMapModal(false);
                toast.success('Zonas de exclusão salvas com sucesso!');
              }}
              onLocationChange={(loc) => {
                setDeliveryConfig(prev => ({
                  ...prev,
                  store_address: {
                    ...prev.store_address,
                    latitude: loc.lat,
                    longitude: loc.lng
                  }
                }));
                setGeocodeSuccess(true);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default DeliveryZonesContent;
